import React, { useState } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Link } from 'react-router-dom';

import menuTypes from '../../../shared/constants/menuTypes';
import MenuTypeIcon from './MenuTileIcon';
import SessionsBanner from './SessionsBanner';
import Actions from './Actions';
import useFetchData from '../../../hooks/useFetchEndpoint';
import DeleteMenu from './ActionModals/DeleteMenu';
import EditMenu from './ActionModals/EditMenu';
import DuplicateMenu from './ActionModals/DuplicateMenu';
import PlatformIcon from '../../../components/PlatformIcon';

const useStyles = makeStyles((theme) => ({
  menuTile: {
    display: 'flex',
    textAlign: 'left',
    flexDirection: ' column',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    minWidth: '16rem',
    maxWidth: '20rem',
    minHeight: '13.25rem',
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
      maxHeight: '10rem',
    },
  },
  menuTileWithBanner: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  menuTitle: {
    ...theme.customFonts.mediumBold,
  },
  menuTileLink: {
    textDecoration: 'none',
    '&:hover': {
      boxShadow: 'none',
      background: 'none',
    },
  },
  menuVariation: {
    ...theme.customFonts.xSmall,
    color: '#5A7296',
  },
  menuTypeContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  menuType: {
    ...theme.customFonts.small,
    lineHeight: '1.43',
  },
  menuTypeInactive: {
    color: '#5A7296',
  },
  inactiveChip: {
    backgroundColor: theme.customPalette.n1,
    color: '#181C43',
    marginLeft: theme.spacing(1),
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  errorChip: {
    backgroundColor: '#CA1D2A',
    color: theme.customPalette.white,
    marginLeft: theme.spacing(1),
  },
  deliveryIcon: {
    marginRight: '4px',
  },
}));

const truncateString = (str) => {
  if (str.length > 25) {
    return `${str.slice(0, 25)}...`;
  }
  return str;
};

const MenuTile = ({ menu, sessionsOwnedMenu = false, isAdmin = false }) => {
  const [actionModal, setActionModal] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    displayMenuName,
    brandMenuVariationName,
    type,
    active,
    kioskEnabled,
    menuName,
    description,
    otterDeliveryEnabled,
    otterPickupEnabled,
    deliverooStoreId,
    justeatStoreId,
    ubereatsStoreId,
    isChurning,
  } = menu;
  const isDeliveryMenu =
    type === menuTypes.DELIVERY.value || type === menuTypes.DELIVERY_INHOUSE.value;
  const classes = useStyles();
  const { data: venueMenuStatus } = useFetchData(`/menus/${menuName}/status`);
  const { publishData = {} } = venueMenuStatus || {};

  return (
    <>
      <Link to={`/menus/${menuName}`} className={classes.menuTileLink}>
        <Paper className={classes.menuTile}>
          <Box className={sessionsOwnedMenu && classes.menuTileWithBanner}>
            <Box>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography variant="h3" className={classes.menuTitle}>
                  {truncateString(displayMenuName)}
                </Typography>
                {isChurning && (
                  <Chip label="Churning" size="small" className={classes.inactiveChip} />
                )}
              </Box>

              {isAdmin && brandMenuVariationName && (
                <Typography variant="body2" className={classes.menuVariation}>
                  {brandMenuVariationName && brandMenuVariationName}
                </Typography>
              )}
              <Box className={classes.menuTypeContainer}>
                <MenuTypeIcon type={type} isActive={active} />
                <Typography
                  variant="body2"
                  className={`${classes.menuType} ${!active && classes.menuTypeInactive}`}
                >
                  {menuTypes[type].label}
                </Typography>
                {!active && (
                  <Chip
                    label={isDeliveryMenu ? 'Paused' : 'Inactive'}
                    size="small"
                    className={classes.inactiveChip}
                  />
                )}
              </Box>
              {kioskEnabled && (
                <Box className={classes.menuTypeContainer} sx={{ mt: 1 }}>
                  <MenuTypeIcon type={menuTypes.KIOSK.value} isActive />
                  <Typography variant="body2" className={`${classes.menuType}`}>
                    {menuTypes.KIOSK.label}
                  </Typography>
                </Box>
              )}
            </Box>
            {sessionsOwnedMenu && <SessionsBanner />}
          </Box>
          <Box
            className={classes.actionContainer}
            sx={{
              '& div:last-child': {
                marginLeft: 'auto',
              },
            }}
          >
            <Actions
              isSessionsMenu={sessionsOwnedMenu}
              setActionModal={setActionModal}
              actionModal={actionModal}
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              menuId={menuName}
            />
            <Box>
              {Object.entries(publishData).map(([key, { isLive }]) => (
                <>
                  {isLive ? (
                    <PlatformIcon
                      platform={key.toLowerCase().replace(/\s+/g, '-')}
                      alt={key}
                      title={`${key} enabled`}
                    />
                  ) : (
                    <PlatformIcon
                      platform={`${key.toLowerCase().replace(/\s+/g, '-')}-grey`}
                      alt={key}
                      title={`${key} disabled`}
                    />
                  )}
                </>
              ))}
            </Box>
          </Box>
        </Paper>
      </Link>
      <DeleteMenu
        open={actionModal === 'DELETE'}
        onClose={() => {
          setActionModal(null);
          setAnchorEl(null);
        }}
        displayMenuName={displayMenuName}
        menuName={menuName}
        active={active}
      />
      <EditMenu
        open={actionModal === 'EDIT_MENU'}
        onClose={() => {
          setActionModal(null);
          setAnchorEl(null);
        }}
        displayMenuName={displayMenuName}
        menuName={menuName}
        active={active}
        description={description}
        type={type}
        otterDeliveryEnabled={otterDeliveryEnabled}
        otterPickupEnabled={otterPickupEnabled}
        kioskEnabled={kioskEnabled}
        deliverooStoreId={deliverooStoreId}
        justeatStoreId={justeatStoreId}
        ubereatsStoreId={ubereatsStoreId}
      />
      <DuplicateMenu
        open={actionModal === 'DUPLICATE'}
        onClose={() => {
          setActionModal(null);
          setAnchorEl(null);
        }}
        displayMenuName={displayMenuName}
        menuName={menuName}
        type={type}
      />
    </>
  );
};

export default MenuTile;
